import React, { useState, useEffect } from 'react';
import { getSystemSettings, updateSystemSettings, getIcsToken } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Settings, Mail, Plus, Trash2, Loader2, Save, Check, BrainCircuit, Calendar, Copy, Smartphone, ExternalLink, Bell } from 'lucide-react';

export const SettingsPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [emails, setEmails] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [aiSchedulingEnabled, setAiSchedulingEnabled] = useState(false);
    const [orderReminderMinutes, setOrderReminderMinutes] = useState(30);
    const [hasChanges, setHasChanges] = useState(false);
    const [icsUrl, setIcsUrl] = useState('');
    const [loadingIcs, setLoadingIcs] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const res = await getSystemSettings();
            setEmails(res.data.order_completion_emails || []);
            setAiSchedulingEnabled(res.data.ai_scheduling_enabled || false);
            setOrderReminderMinutes(res.data.order_reminder_minutes || 30);
        } catch (err) {
            toast.error('Fehler beim Laden der Einstellungen');
        } finally {
            setLoading(false);
        }
    };

    const addEmail = () => {
        if (!newEmail.trim()) return;
        
        // Simple email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmail.trim())) {
            toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
            return;
        }
        
        if (emails.includes(newEmail.trim().toLowerCase())) {
            toast.error('Diese E-Mail-Adresse ist bereits vorhanden');
            return;
        }
        
        setEmails([...emails, newEmail.trim().toLowerCase()]);
        setNewEmail('');
        setHasChanges(true);
    };

    const removeEmail = (index) => {
        const newEmails = emails.filter((_, i) => i !== index);
        setEmails(newEmails);
        setHasChanges(true);
    };

    const handleSave = async () => {
        if (emails.length === 0) {
            toast.error('Mindestens eine E-Mail-Adresse erforderlich');
            return;
        }
        
        setSaving(true);
        try {
            await updateSystemSettings({ 
                order_completion_emails: emails,
                ai_scheduling_enabled: aiSchedulingEnabled,
                order_reminder_minutes: orderReminderMinutes
            });
            toast.success('Einstellungen gespeichert');
            setHasChanges(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addEmail();
        }
    };
    
    const generateIcsUrl = async () => {
        setLoadingIcs(true);
        try {
            const res = await getIcsToken();
            const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            // feed_url already contains /api prefix
            const fullUrl = `${baseUrl}${res.data.feed_url}`;
            setIcsUrl(fullUrl);
        } catch (err) {
            toast.error('Fehler beim Generieren des Kalender-Links');
        } finally {
            setLoadingIcs(false);
        }
    };
    
    const copyIcsUrl = () => {
        navigator.clipboard.writeText(icsUrl);
        toast.success('Kalender-Link kopiert!');
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="settings-page">
            {/* Header */}
            <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10">
                    <Settings className="h-6 w-6 text-primary" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold">Einstellungen</h1>
                    <p className="text-muted-foreground">Systemweite Konfigurationen</p>
                </div>
            </div>

            {/* AI Scheduling Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <BrainCircuit className="h-5 w-5" />
                        KI-Auftragsplanung
                    </CardTitle>
                    <CardDescription>
                        Nutzen Sie künstliche Intelligenz, um optimale Mitarbeiter und Termine für neue Aufträge vorzuschlagen.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${aiSchedulingEnabled ? 'bg-green-100' : 'bg-muted'}`}>
                                <BrainCircuit className={`h-5 w-5 ${aiSchedulingEnabled ? 'text-green-600' : 'text-muted-foreground'}`} />
                            </div>
                            <div>
                                <p className="font-medium">KI-Planung aktivieren</p>
                                <p className="text-sm text-muted-foreground">
                                    {aiSchedulingEnabled ? 'KI-Vorschläge sind verfügbar' : 'KI-Planung ist deaktiviert'}
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={aiSchedulingEnabled}
                            onCheckedChange={(checked) => {
                                setAiSchedulingEnabled(checked);
                                setHasChanges(true);
                            }}
                            data-testid="ai-scheduling-switch"
                        />
                    </div>
                    
                    {aiSchedulingEnabled && (
                        <div className="text-sm text-muted-foreground space-y-2 p-4 border rounded-lg bg-primary/5">
                            <p className="font-medium text-primary">So funktioniert die KI-Planung:</p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Beim Erstellen eines neuen Auftrags erscheint der Button "KI-Planung starten"</li>
                                <li>Die KI analysiert Mitarbeiter-Verfügbarkeit, Arbeitszeiten und bestehende Termine</li>
                                <li>Sie erhalten einen Vorschlag für Mitarbeiter und Datum</li>
                                <li>Sie können den Vorschlag übernehmen oder anpassen</li>
                            </ul>
                        </div>
                    )}
                    
                    {/* Save Button for AI Settings */}
                    <div className="flex justify-end pt-4 border-t">
                        <Button 
                            onClick={handleSave} 
                            disabled={saving || !hasChanges}
                            data-testid="save-ai-settings-btn"
                        >
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : hasChanges ? (
                                <Save className="h-4 w-4 mr-2" />
                            ) : (
                                <Check className="h-4 w-4 mr-2" />
                            )}
                            {hasChanges ? 'Änderungen speichern' : 'Gespeichert'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Email Recipients Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        E-Mail-Empfänger für Auftragsabschluss
                    </CardTitle>
                    <CardDescription>
                        Diese E-Mail-Adressen erhalten eine Benachrichtigung mit PDF-Bericht, wenn ein Auftrag abgeschlossen wird.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Current Emails */}
                    <div className="space-y-2">
                        <Label>Aktuelle Empfänger</Label>
                        {emails.length === 0 ? (
                            <p className="text-muted-foreground text-sm py-4 text-center border rounded-sm">
                                Keine E-Mail-Empfänger konfiguriert
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {emails.map((email, idx) => (
                                    <div 
                                        key={email} 
                                        className="flex items-center justify-between p-3 bg-muted/50 rounded-sm"
                                        data-testid={`email-recipient-${email}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Mail className="h-4 w-4 text-muted-foreground" />
                                            <span className="font-mono text-sm">{email}</span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeEmail(idx)}
                                            data-testid={`remove-email-${email}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Add New Email */}
                    <div className="space-y-2">
                        <Label htmlFor="new-email">Neue E-Mail-Adresse hinzufügen</Label>
                        <div className="flex gap-2">
                            <Input
                                id="new-email"
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="z.B. buchhaltung@firma.de"
                                className="flex-1"
                                data-testid="new-email-input"
                            />
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={addEmail}
                                data-testid="add-email-btn"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Hinzufügen
                            </Button>
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-4 border-t">
                        <Button 
                            onClick={handleSave} 
                            disabled={saving || !hasChanges}
                            data-testid="save-settings-btn"
                        >
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : hasChanges ? (
                                <Save className="h-4 w-4 mr-2" />
                            ) : (
                                <Check className="h-4 w-4 mr-2" />
                            )}
                            {hasChanges ? 'Änderungen speichern' : 'Gespeichert'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                    <div className="flex gap-3">
                        <Mail className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <p className="font-medium text-primary mb-1">Hinweis zum E-Mail-Versand</p>
                            <p className="text-muted-foreground">
                                Wenn ein Mitarbeiter einen Auftrag abschließt, wird automatisch eine E-Mail mit dem 
                                Auftragsabschluss-Bericht als PDF an alle hier konfigurierten Empfänger gesendet. 
                                Der Bericht enthält Informationen zu den erledigten Arbeitspositionen, Arbeitszeiten 
                                und zusätzlichen Bemerkungen.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Push Reminder Settings Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Bell className="h-5 w-5" />
                        Auftrags-Erinnerungen
                    </CardTitle>
                    <CardDescription>
                        Konfigurieren Sie, wie lange vor einem Termin die Push-Benachrichtigung gesendet wird.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label>Erinnerung senden vor Termin</Label>
                        <Select 
                            value={orderReminderMinutes.toString()} 
                            onValueChange={(value) => {
                                setOrderReminderMinutes(parseInt(value));
                                setHasChanges(true);
                            }}
                        >
                            <SelectTrigger className="w-full sm:w-48" data-testid="reminder-minutes-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="15">15 Minuten</SelectItem>
                                <SelectItem value="30">30 Minuten</SelectItem>
                                <SelectItem value="45">45 Minuten</SelectItem>
                                <SelectItem value="60">1 Stunde</SelectItem>
                                <SelectItem value="120">2 Stunden</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                            Mitarbeiter erhalten eine Push-Benachrichtigung auf ihr Smartphone, bevor ein geplanter Auftrag beginnt.
                        </p>
                    </div>
                    
                    {/* Save Button for reminder settings */}
                    <div className="flex justify-end pt-4 border-t">
                        <Button 
                            onClick={handleSave} 
                            disabled={saving || !hasChanges}
                            size="sm"
                        >
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Save className="h-4 w-4 mr-2" />
                            )}
                            Speichern
                        </Button>
                    </div>
                </CardContent>
            </Card>
            
            {/* Calendar Sync Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        Kalender-Synchronisierung
                    </CardTitle>
                    <CardDescription>
                        Synchronisieren Sie Ihren Thut Manager Kalender mit Ihrem iPhone, iPad oder anderen Kalender-Apps.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {!icsUrl ? (
                        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                            <Button 
                                onClick={generateIcsUrl}
                                disabled={loadingIcs}
                                data-testid="generate-ics-btn"
                            >
                                {loadingIcs ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                    <Calendar className="h-4 w-4 mr-2" />
                                )}
                                Kalender-Link generieren
                            </Button>
                            <span className="text-sm text-muted-foreground">
                                Erstellen Sie einen persönlichen Link für Ihre Kalender-App
                            </span>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex flex-col gap-2">
                                <Label>Ihr persönlicher Kalender-Link:</Label>
                                <div className="flex gap-2">
                                    <Input 
                                        value={icsUrl} 
                                        readOnly 
                                        className="font-mono text-xs"
                                        data-testid="ics-url-input"
                                    />
                                    <Button 
                                        variant="outline" 
                                        onClick={copyIcsUrl}
                                        data-testid="copy-ics-btn"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                                <p className="font-medium text-blue-800 flex items-center gap-2">
                                    <Smartphone className="h-4 w-4" />
                                    So fügen Sie den Kalender auf Ihrem iPhone hinzu:
                                </p>
                                <ol className="list-decimal list-inside text-sm text-blue-700 space-y-2">
                                    <li>Kopieren Sie den Link oben</li>
                                    <li>Öffnen Sie <strong>Einstellungen</strong> auf Ihrem iPhone</li>
                                    <li>Gehen Sie zu <strong>Kalender</strong> → <strong>Accounts</strong></li>
                                    <li>Tippen Sie auf <strong>Account hinzufügen</strong> → <strong>Andere</strong></li>
                                    <li>Wählen Sie <strong>Kalenderabo hinzufügen</strong></li>
                                    <li>Fügen Sie den kopierten Link ein und tippen Sie auf <strong>Weiter</strong></li>
                                </ol>
                                <p className="text-xs text-blue-600 mt-2">
                                    ℹ️ Der Kalender aktualisiert sich automatisch (ca. alle 15 Minuten). 
                                    Änderungen müssen weiterhin in Thut Manager vorgenommen werden.
                                </p>
                            </div>
                            
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    onClick={generateIcsUrl}
                                    size="sm"
                                >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Neuen Link generieren
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default SettingsPage;
